import { useState, useEffect } from 'react';

interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  status: string;
  locationId?: string;
  crmId?: string;
  franchiseId?: string;
  lastLogin?: string;
  roleId?: string;
}

interface UseUserInfoReturn {
  user: UserData | null;
  isAuthenticated: boolean;
  loading: boolean;
  locationId: string | null;
  refreshUser: () => void;
}

/**
 * Hook to get user information from localStorage
 * Similar to getMemberInfo pattern from Maatra project
 * Provides reactive user state across the application
 */
export const useUserInfo = (): UseUserInfoReturn => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  const getUserFromStorage = (): UserData | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  };

  const refreshUser = () => {
    const userData = getUserFromStorage();
    setUser(userData);
    setLoading(false);
  };

  useEffect(() => {
    // Initial load
    refreshUser();

    // Listen for storage changes (for multi-tab support)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        refreshUser();
      }
    };

    globalThis.addEventListener('storage', handleStorageChange);
    return () => globalThis.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    loading,
    locationId: user?.locationId || null,
    refreshUser,
  };
};

export type { UserData };
